import { useCallback } from 'react'
import { useSearchParams } from '@/routing/navigation'
import useSWR, { mutate as globalMutate } from 'swr'
import { usersApi } from '@/lib/api'
import { useAuth } from '@/lib/config/auth-provider'

export interface OnboardingState {
  hasSeenWelcome: boolean
  currentPhase: 'welcome' | 'complete' | 'idle'
}

interface OnboardingApiData {
  hasCompletedOnboarding?: boolean
  onboardingStep?: number
  isFirstTime?: boolean
  companyName?: string
  role?: string
}

const fetchOnboardingState = async (
  userId?: string,
): Promise<OnboardingApiData | null> => {
  const onboardingHandoff = localStorage.getItem('showOnboardingTour')
  const belongsToCurrentUser =
    onboardingHandoff === 'true' ||
    (!!userId && onboardingHandoff === String(userId))

  if (belongsToCurrentUser) {
    // Older producers and the `?newUser=1` redirect use the unscoped value.
    // Bind it as soon as the authenticated profile is available so another
    // account in the same browser cannot inherit this unfinished onboarding.
    if (onboardingHandoff === 'true' && userId) {
      localStorage.setItem('showOnboardingTour', String(userId))
    }
    return {
      hasCompletedOnboarding: false,
      onboardingStep: 0,
      isFirstTime: true,
    }
  }

  return usersApi.getOnboardingState()
}

const DEV_ONBOARDING_KEY = 'dev-onboarding-preview'

export function useOnboarding() {
  const searchParams = useSearchParams()
  const { isAuthenticated, isLoading: authLoading, user } = useAuth()

  // Persist the dev flag to localStorage so it survives auth redirects
  // (/?dev-onboarding=true → /login → / loses the query param).
  // Accepts "true" (normal flow) or "invited" (simulate invited user).
  const devParam =
    process.env.NODE_ENV === 'development'
      ? searchParams.get('dev-onboarding')
      : null

  // When the URL param is freshly provided, force-reset so it works
  // even after a previous test run completed onboarding.
  const isFreshDevParam = devParam === 'true' || devParam === 'invited'
  if (isFreshDevParam) {
    localStorage.setItem(DEV_ONBOARDING_KEY, devParam)
  }

  const storedDevValue = localStorage.getItem(DEV_ONBOARDING_KEY)
  const isDevMode =
    process.env.NODE_ENV === 'development' &&
    (storedDevValue === 'true' || storedDevValue === 'invited')

  const shouldFetch = isAuthenticated && !authLoading

  const { data, isLoading, mutate } = useSWR(
    shouldFetch ? 'onboarding-state' : null,
    () => fetchOnboardingState(user?.id),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
    },
  )

  const state: OnboardingState = (() => {
    if (isDevMode) {
      // If the URL param was just provided, always show the wizard
      // regardless of completion state (allows re-testing).
      if (isFreshDevParam) {
        if (!data) {
          return { hasSeenWelcome: true, currentPhase: 'idle' as const }
        }
        return { hasSeenWelcome: false, currentPhase: 'welcome' as const }
      }
      // Stored dev flag (survived a redirect) — respect completion
      // so navigating away doesn't re-trigger the wizard.
      if (
        data?.hasCompletedOnboarding ||
        data?.onboardingStep === 99 ||
        data?.onboardingStep === -1
      ) {
        localStorage.removeItem(DEV_ONBOARDING_KEY)
        return { hasSeenWelcome: true, currentPhase: 'idle' as const }
      }
      if (!data) {
        return { hasSeenWelcome: true, currentPhase: 'idle' as const }
      }
      return { hasSeenWelcome: false, currentPhase: 'welcome' as const }
    }

    if (!data) {
      return { hasSeenWelcome: true, currentPhase: 'idle' as const }
    }

    if (data.isFirstTime) {
      return { hasSeenWelcome: false, currentPhase: 'welcome' as const }
    }

    if (
      data.hasCompletedOnboarding ||
      data.onboardingStep === 99 ||
      data.onboardingStep === -1
    ) {
      return { hasSeenWelcome: true, currentPhase: 'idle' as const }
    }

    // In progress (step 1+, not completed) — resume wizard.
    // Step 0 is the default DB value and means "never started"; the
    // wizard is only entered via the isFirstTime / localStorage path.
    if (typeof data.onboardingStep === 'number' && data.onboardingStep > 0) {
      return { hasSeenWelcome: false, currentPhase: 'welcome' as const }
    }

    return { hasSeenWelcome: true, currentPhase: 'idle' as const }
  })()

  const completeWelcome = useCallback(async () => {
    localStorage.removeItem(DEV_ONBOARDING_KEY)
    mutate(
      { ...data, hasCompletedOnboarding: true, onboardingStep: 99 },
      false,
    )
    try {
      await usersApi.updateOnboardingState({
        hasCompletedOnboarding: true,
        onboardingStep: 99,
      })
      await globalMutate('onboarding-state')
      await globalMutate('/users/me')
      await globalMutate('home-targeting-preferences')
      await globalMutate('featured-twins')
      // Re-fetch audiences so newly auto-bookmarked ones surface immediately
      // in the audiences lobby without requiring a manual page refresh.
      globalMutate('home-audiences')
    } catch (error) {
      mutate()
      throw error
    }
  }, [data, mutate])

  const skipOnboarding = useCallback(async () => {
    localStorage.removeItem(DEV_ONBOARDING_KEY)
    mutate(
      { ...data, hasCompletedOnboarding: true, onboardingStep: -1 },
      false,
    )
    try {
      await usersApi.updateOnboardingState({
        hasCompletedOnboarding: true,
        onboardingStep: -1,
      })
      await globalMutate('onboarding-state')
      await globalMutate('/users/me')
      await globalMutate('featured-twins')
    } catch (error) {
      mutate()
      throw error
    }
  }, [data, mutate])

  const restartOnboarding = useCallback(() => {
    mutate(
      {
        ...data,
        hasCompletedOnboarding: false,
        onboardingStep: 0,
        isFirstTime: true,
      },
      false,
    )
  }, [data, mutate])

  return {
    ...state,
    isLoading: isLoading || authLoading,
    showWelcome: state.currentPhase === 'welcome',
    completeWelcome,
    skipOnboarding,
    restartOnboarding,
  }
}
